import Grid from '@mui/material/Grid';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete.tsx';
import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize.tsx';
import { TextField } from '@openx/components-core/lib/TextField/TextField.tsx';
import type { ContextualSegmentsDrawerState } from '@openx/types/targeting/advancedTargeting.ts';
import type { SegmentsOption } from '@openx/types/targeting/customVariables.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetContextualData } from '../hooks/useGetContextualData.ts';

import { comparisonOptionLabels, filteredTypeOptions } from './constants.ts';
import { filterSegmentsOptions } from './utils.ts';

export type ContextualSegmentsTopBoxProps = {
  items: ContextualSegmentsDrawerState;
  updateParams: Dispatch<SetStateAction<ContextualSegmentsDrawerState>>;
  accountUid: string;
  /** Narrows the box to fit next to the targeting type selector. */
  besideTypeSelector?: boolean;
};

export const ContextualSegmentsTopBoxV2 = ({
  items,
  updateParams,
  accountUid,
  besideTypeSelector = false,
}: ContextualSegmentsTopBoxProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [comparisonType, setComparisonType] = useState<ComparisonType>();
  const { t } = useTranslation();

  const optionLabels = useMemo(() => comparisonOptionLabels(), []);
  const { segments, loading, providerId, providerName } = useGetContextualData(accountUid);

  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary deps are included intentionally
  useEffect(() => {
    if (providerId) {
      updateParams(prev => ({
        ...prev,
        contextualV2: {
          ...prev.contextualV2,
          attr: providerId,
        },
      }));
    }
  }, [providerId, providerName, updateParams]);

  const filteredOptions = useMemo(
    () => filterSegmentsOptions(items.contextualV2, segments, searchInput),
    [items.contextualV2, searchInput, segments]
  );

  const handleAddSegment = (value: SegmentsOption | null) => {
    if (!value) return;

    const inclusionKey = comparisonType === ComparisonType.INTERSECTS ? 'includes' : 'excludes';

    updateParams(prev => {
      const prevSegmentValue = prev?.contextualV2?.[inclusionKey] || [];
      const udpatedSegments = [...prevSegmentValue, value.id];

      return {
        ...prev,
        contextualV2: {
          ...prev.contextualV2,
          attr: providerId ?? '',
          [inclusionKey]: udpatedSegments,
        },
      };
    });
  };

  const noSegments = segments.length === 0;
  const notApplicablePlaceholder = t('N / A');
  const providerDisplayName = providerName ?? providerId ?? notApplicablePlaceholder;

  return (
    <>
      <Grid size={besideTypeSelector ? 8 : 12} sx={besideTypeSelector ? { paddingLeft: '0.8rem' } : undefined}>
        <Grid container spacing={1} sx={{ border: 'none', marginTop: theme => -theme.spacing(1) }}>
          <Grid size={6}>
            <TextField
              sx={{ width: '100%' }}
              data-test={'provider-section'}
              name="contextual_provider"
              value={providerDisplayName}
              placeholder={providerDisplayName}
              label={t('Provider')}
              disabled
            />
          </Grid>

          <Grid size={6}>
            <AutoComplete
              options={filteredTypeOptions}
              renderOptions={{
                dataTest: 'comparison-option',
              }}
              getOptionLabel={option => optionLabels[option]}
              textFieldReadOnly
              disableClearable
              data-test={'comparison-options'}
              disabled={!providerName || noSegments || loading}
              textFieldProps={{
                label: t('Comparison Type'),
                placeholder: !providerName ? notApplicablePlaceholder : t('Select Comparison Type'),
              }}
              onChange={(_, value) => setComparisonType(value)}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        size={12}
        sx={{ border: 'none', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(1) }}
      >
        <AutoCompleteVirtualize
          loading={loading}
          disabled={!comparisonType || noSegments || loading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          textFieldProps={{
            label: t('Segment'),
            placeholder: !providerName ? notApplicablePlaceholder : t('Select Segment'),
          }}
          onChange={(_, value) => {
            handleAddSegment(value);
            setSearchInput('');
          }}
          getOptionLabel={option => option.name ?? ''}
          options={filteredOptions}
          data-test="contextual-segments-dropdown"
          onInputChange={(_, option) => setSearchInput(option)}
          inputValue={searchInput}
          value={null}
        />
      </Grid>
    </>
  );
};
